<template>
    <b-autocomplete
      :rounded="rounded"
      field="label"
      v-model="searchTerm"
      :data="documentTypes"
      placeholder="Search for document types..."
      icon="search"
      icon-pack="fas"
      clearable
      @select="option => selectDocumentType(option)"
      @input="loadResults"
      @keyup.native.enter="handleEnterKey"
      check-infinite-scroll
      :size="size || 'is-small'"
      max-height="350px"
      :loading="loading"
    >
      <template slot-scope="props">
        <div class="document-type-option">
            <div class="document-type-name">
                <strong>{{ props.option.display_name }}</strong>
            </div>
            <div class="document-type-context">
                <small class="has-text-grey">{{ props.option.context }}</small>
            </div>
            <div v-if="props.option.description" class="document-type-description">
                <small class="is-italic">{{ props.option.description }}</small>
            </div>
        </div>
      </template>
      <template slot="empty">
        <div class="has-text-centered">
          <p v-if="searchTerm.length > 0">
            No document types found for "{{ searchTerm }}"
          </p>
          <p v-else>Start typing to search document types...</p>
          
          <!-- Show option to create custom when there are no results -->
          <div v-if="searchTerm.length > 2 && documentTypes.length === 0" class="custom-option-hint">
            <hr class="my-2">
            <button 
              @click="createCustomDocumentType"
              class="button is-small is-outlined is-primary"
              type="button"
            >
              <span class="icon is-small">
                <i class="fas fa-plus"></i>
              </span>
              <span>Create "{{ searchTerm }}" as custom document</span>
            </button>
            <p class="help is-size-7 mt-1">Or press Enter to add as custom document</p>
          </div>
        </div>
      </template>
    </b-autocomplete>
</template>

<script>
import { apiFetch } from '@frontend/api/api';

export default {
  name: 'DocumentTypeTypeahead',
  data() {
    return {
      documentTypes: [],
      loading: false,
      searchTerm: '',
      abortControllers: [],
      // Cache frequently used document types for better UX
      recentlyUsed: [],
    };
  },
  props: {
    // Callback function to handle selection
    callback: {
      type: Function,
      required: true
    },
    // Size of the input
    size: {
      type: String,
      default: 'is-small'
    },
    // Whether input should be rounded
    rounded: {
      type: Boolean,
      default: false,
    },
    // Placeholder text
    placeholder: {
      type: String,
      default: 'Search for document types...'
    },
    // Initial value to display
    initialValue: {
      type: String,
      default: ''
    },
    // Limit number of results
    limit: {
      type: Number,
      default: 20
    }
  },
  mounted() {
    if (this.initialValue) {
      this.searchTerm = this.initialValue;
    }
    // Load some initial results when component mounts
    this.loadResults();
  },
  methods: {
    selectDocumentType(option) {
      if (option) {
        // Add to recently used for better UX
        this.addToRecentlyUsed(option);
        
        // Call the parent callback with the selected document type
        this.callback.call(this, {
          id: option.value,
          display_name: option.display_name,
          name: option.name,
          category: option.category,
          full_option: option
        });
      }
    },
    
    async loadResults() {
      // Cancel any pending requests
      this.abortControllers.forEach(controller => controller.abort());
      this.abortControllers = [];
      
      const abortController = new AbortController();
      this.abortControllers.push(abortController);
      
      this.loading = true;
      
      try {
        // Build query parameters
        const params = new URLSearchParams({
          limit: this.limit.toString()
        });
        
        if (this.searchTerm && this.searchTerm.trim()) {
          params.append('search', this.searchTerm.trim());
        }
        
        const response = await apiFetch(`/api/v1/client-portal/document-types/?${params}`, {
          signal: abortController.signal,
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const jsonData = await response.json();
        
        // Merge with recently used items if search is empty
        if (!this.searchTerm || this.searchTerm.trim() === '') {
          this.documentTypes = this.mergeWithRecentlyUsed(jsonData.document_types);
        } else {
          this.documentTypes = jsonData.document_types;
        }
        
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Document type search aborted');
        } else {
          console.error('Error loading document types:', error);
          this.documentTypes = [];
        }
      } finally {
        this.loading = false;
      }
    },
    
    addToRecentlyUsed(option) {
      // Remove if already exists
      this.recentlyUsed = this.recentlyUsed.filter(item => item.value !== option.value);
      
      // Add to beginning
      this.recentlyUsed.unshift(option);
      
      // Keep only last 5 items
      this.recentlyUsed = this.recentlyUsed.slice(0, 5);
      
      // Store in localStorage for persistence
      try {
        localStorage.setItem('recentDocumentTypes', JSON.stringify(this.recentlyUsed));
      } catch (e) {
        console.warn('Could not save recent document types to localStorage');
      }
    },
    
    loadRecentlyUsed() {
      try {
        const stored = localStorage.getItem('recentDocumentTypes');
        if (stored) {
          this.recentlyUsed = JSON.parse(stored);
        }
      } catch (e) {
        console.warn('Could not load recent document types from localStorage');
        this.recentlyUsed = [];
      }
    },
    
    mergeWithRecentlyUsed(apiResults) {
      // If we have recently used items, show them first when no search term
      if (this.recentlyUsed.length > 0) {
        const recentIds = new Set(this.recentlyUsed.map(item => item.value));
        const otherResults = apiResults.filter(item => !recentIds.has(item.value));
        
        return [
          ...this.recentlyUsed.map(item => ({ ...item, isRecent: true })),
          ...otherResults
        ];
      }
      
      return apiResults;
    },
    
    handleEnterKey() {
      // Handle Enter key press - create custom document if no selection is made
      if (this.searchTerm && this.searchTerm.trim()) {
        // Check if the current search term exactly matches any of the visible results
        const exactMatch = this.documentTypes.find(dt => 
          dt.display_name.toLowerCase() === this.searchTerm.toLowerCase() ||
          dt.name.toLowerCase() === this.searchTerm.toLowerCase()
        );
        
        if (exactMatch) {
          // If there's an exact match, select it
          this.selectDocumentType(exactMatch);
        } else {
          // Otherwise, create a custom document type
          this.createCustomDocumentType();
        }
      }
    },
    
    createCustomDocumentType() {
      // Create a custom document type entry
      if (this.searchTerm && this.searchTerm.trim()) {
        const customDocumentType = {
          id: null, // No ID for custom types
          display_name: this.searchTerm.trim(),
          name: this.searchTerm.trim(),
          category: 'custom',
          is_custom: true,
          full_option: null
        };
        
        // Add to recently used for better UX
        this.addToRecentlyUsed({
          ...customDocumentType,
          value: null,
          label: customDocumentType.display_name,
          context: 'Custom Document'
        });
        
        // Call the callback with the custom document type
        this.callback.call(this, customDocumentType);
        
        // Clear the search term
        this.searchTerm = '';
      }
    }
  },
  
  created() {
    this.loadRecentlyUsed();
  }
};
</script>

<style scoped>
.document-type-option {
  padding: 4px 0;
}

.document-type-name {
  line-height: 1.2;
}

.document-type-context {
  margin-top: 2px;
  font-size: 0.875rem;
}

.document-type-description {
  margin-top: 4px;
  font-size: 0.8rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Add a subtle indicator for recently used items */
.document-type-option:has(.isRecent) {
  border-left: 2px solid #3273dc;
  padding-left: 8px;
}

/* Custom option hint styling */
.custom-option-hint {
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin-top: 4px;
}

.custom-option-hint .button {
  margin-bottom: 4px;
}
</style> 